import { useState, useEffect, useMemo, useRef } from 'react';
export enum ScrollDirection {
  Up = 'SCROLL_UP',
  Down = 'SCROLL_DOWN',
}
interface ThrottleOptions {
  leading?: boolean;
  trailing?: boolean;
}
function throttle<T extends (...args: any[]) => any>(func: T, wait: number, options: ThrottleOptions = {}): T & {
  cancel: () => void;
} {
  let timeout: NodeJS.Timeout | null = null;
  let previous = 0;
  let result: ReturnType<T>;
  const {
    leading = true,
    trailing = true
  } = options;
  function throttled(this: any, ...args: Parameters<T>): ReturnType<T> | undefined {
    const now = Date.now();
    if (!previous && !leading) {
      previous = now;
    }
    const remaining = wait - (now - previous);
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      result = func.apply(this, args);
    } else if (!timeout && trailing) {
      timeout = setTimeout(() => {
        previous = !leading ? 0 : Date.now();
        timeout = null;
        result = func.apply(this, args);
      }, remaining);
    }
    return result;
  }
  throttled.cancel = function () {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    previous = 0;
  };
  return throttled as T & {
    cancel: () => void;
  };
}
export function useVerticalScrollDirection(threshhold: number, enabled = true) {
  const [direction, setDirection] = useState<ScrollDirection>(ScrollDirection.Up);
  // saving the current scroll position in a ref prevents rerender of the navigation on each scroll event.
  const currentScrollYRef = useRef<number>(0);
  if (!enabled && direction !== ScrollDirection.Up) {
    setDirection(ScrollDirection.Up);
  }
  const handleScroll = useMemo(() => throttle(
  // eslint-disable-next-line react-hooks/refs
  () => {
    if (threshhold < Math.abs(window.scrollY - currentScrollYRef.current)) {
      if (window.scrollY > currentScrollYRef.current) {
        // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
        setDirection(ScrollDirection.Down);
      } else if (window.scrollY < currentScrollYRef.current) {
        // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
        setDirection(ScrollDirection.Up);
      }
      currentScrollYRef.current = window.scrollY;
    }
  }, 16, {
    leading: true,
    trailing: true
  }), [currentScrollYRef, threshhold]);
  useEffect(() => {
    if (enabled) {
      if (typeof window !== 'undefined') {
        handleScroll();
        window.addEventListener('scroll', handleScroll, {
          passive: true
        });
      }
      return () => {
        handleScroll.cancel();
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      currentScrollYRef.current = 0;
      return () => handleScroll.cancel();
    }
  }, [enabled, handleScroll]);
  return direction;
}